import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { z } from "zod";
import { getUnsubscribeStatus, unsubscribeByToken } from "@/lib/newsletter.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: z.object({ token: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "Unsubscribe from the Investor Newsletter | DSCR Loan Market" },
      { name: "description", content: "Stop receiving the Simply Approved Mortgages investor newsletter." },
      { property: "og:title", content: "Unsubscribe | DSCR Loan Market" },
      { property: "og:description", content: "Manage your investor newsletter subscription." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const getStatus = useServerFn(getUnsubscribeStatus);
  const unsubscribe = useServerFn(unsubscribeByToken);
  const [state, setState] = useState<"loading" | "invalid" | "ready" | "working" | "done" | "error">("loading");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token || !/^[a-f0-9]{32,128}$/i.test(token)) { setState("invalid"); return; }
    getStatus({ data: { token } })
      .then((r) => { if (!r.found) setState("invalid"); else { setEmail(r.email); setState(r.unsubscribed ? "done" : "ready"); } })
      .catch(() => setState("error"));
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setState("working");
    try {
      const r = await unsubscribe({ data: { token } });
      setState(r.ok ? "done" : "invalid");
    } catch { setState("error"); }
  };

  return (
    <main className="mx-auto max-w-lg px-4 py-16">
      <h1 className="font-display text-2xl font-bold text-primary">Newsletter subscription</h1>
      {state === "loading" && <p className="mt-4 text-muted-foreground">Checking your link…</p>}
      {state === "invalid" && <p className="mt-4 text-muted-foreground">This unsubscribe link is not valid or has expired. If you keep receiving emails, contact us and we will remove you.</p>}
      {state === "error" && <p className="mt-4 text-destructive">We couldn't process that right now. Please try again in a moment.</p>}
      {(state === "ready" || state === "working") && (
        <div className="mt-4 space-y-4">
          <p className="text-muted-foreground">Stop sending the investor newsletter to <strong className="text-foreground">{email}</strong>?</p>
          <Button onClick={confirm} disabled={state === "working"}>{state === "working" ? "Unsubscribing…" : "Unsubscribe"}</Button>
        </div>
      )}
      {state === "done" && (
        <p className="mt-4 text-muted-foreground">{email ? <><strong className="text-foreground">{email}</strong> is </> : "You are "}unsubscribed and will not receive future newsletters. Changed your mind? You can sign up again from any newsletter form on the <Link to="/" className="text-primary underline">homepage</Link>.</p>
      )}
    </main>
  );
}
